import type { ChangeEvent, ReactNode } from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { Briefcase, GraduationCap, IdCard, Landmark, Phone, type LucideIcon } from 'lucide-react';

import { AadhaarInput } from '@/components/ui/AadhaarInput';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import type { Option } from '@/constants/institute';
import { toMobileDigits } from '@/lib/forms';
import { cn } from '@/lib/utils';
import { EMPLOYMENT_TYPE_OPTIONS, SALARY_PAYMENT_MODE_OPTIONS } from '../constants';
import type { StaffFormValues } from '../schemas/staff.schema';

/**
 * The staff form's panel. One component, so the sections below and the form's
 * own cards share a single header treatment rather than each spelling it out.
 */
const CARD_TONE = {
  emerald: 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-200/80 dark:border-emerald-800/80 text-emerald-600 dark:text-emerald-400',
  blue: 'bg-blue-50 dark:bg-blue-950/60 border-blue-200/80 dark:border-blue-800/80 text-blue-600 dark:text-blue-400',
  purple: 'bg-purple-50 dark:bg-purple-950/60 border-purple-200/80 dark:border-purple-800/80 text-purple-600 dark:text-purple-400',
  amber: 'bg-amber-50 dark:bg-amber-950/60 border-amber-200/80 dark:border-amber-800/80 text-amber-600 dark:text-amber-400',
  rose: 'bg-rose-50 dark:bg-rose-950/60 border-rose-200/80 dark:border-rose-800/80 text-rose-600 dark:text-rose-400',
  teal: 'bg-teal-50 dark:bg-teal-950/60 border-teal-200/80 dark:border-teal-800/80 text-teal-600 dark:text-teal-400',
} as const;

/**
 * The first choice of every optional select. `Select`'s placeholder cannot be
 * chosen again once something is picked, so without this a field set by
 * mistake could never be cleared.
 */
export const NOT_SET_OPTION: Option = { value: '', label: 'Not set' };

export interface StaffFormCardProps {
  icon: LucideIcon;
  tone: keyof typeof CARD_TONE;
  title: string;
  description: string;
  children: ReactNode;
}

export function StaffFormCard({ icon: Icon, tone, title, description, children }: StaffFormCardProps) {
  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xs">
      <div className="flex items-center gap-3 mb-3.5">
        <div className={cn('p-2 rounded-xl border shrink-0', CARD_TONE[tone])}>
          <Icon className="w-5 h-5" aria-hidden />
        </div>
        <div>
          <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">{title}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export interface StaffDetailsFieldsProps {
  register: UseFormRegister<StaffFormValues>;
  errors: FieldErrors<StaffFormValues>;
  /** Everybody this person could report to — never themselves. */
  reportingOptions: Option[];
  isLoadingReporting: boolean;
  /**
   * A new account: the enrolment fields carry a star, as the create schema
   * demands them (`ENROLMENT_DETAILS`). An edit leaves them optional.
   */
  isEnrolling?: boolean;
  /** The payment mode chosen, so bank transfer can star the bank fields. */
  salaryPaymentMode?: string;
}

/**
 * The staff profile's details beyond the account: identity numbers, contact and
 * address, bank, employment and background. Personal details (date of birth,
 * gender, blood group…) sit in the form's own Personal card beside the photo.
 *
 * Enrolling a new person makes the HR record mandatory — PAN, emergency
 * contact, present address, employment type and how the salary is paid — and
 * bank transfer then requires the bank details. An edit keeps them optional, so
 * an older record can still be saved. The identity numbers, bank account,
 * phones and addresses are encrypted by the server and masked to anyone who
 * may only view the profile.
 */
export function StaffDetailsFields({
  register,
  errors,
  reportingOptions,
  isLoadingReporting,
  isEnrolling = false,
  salaryPaymentMode,
}: StaffDetailsFieldsProps) {
  const fieldErrors = errors.details;
  const isBankTransfer = salaryPaymentMode === 'BANK_TRANSFER';
  const isCash = salaryPaymentMode === 'CASH';

  /**
   * Cash needs no account, so the bank inputs leave the form. `shouldUnregister`
   * drops their values with them: a half-typed IFSC cannot then block the save
   * with an error nobody can see, and a record switched to cash stops carrying a
   * stale account. Switching back to transfer refills the saved account.
   */
  function bankField(name: 'details.bankName' | 'details.bankAccountHolder' | 'details.bankAccountNumber' | 'details.bankIfsc') {
    return register(name, { shouldUnregister: true });
  }

  /** A ten-digit box: digits only, a pasted +91 or 0 dropped as it is typed. */
  function mobileField(name: 'details.alternateMobile' | 'details.emergencyContactMobile') {
    const field = register(name);
    return {
      ...field,
      inputMode: 'numeric' as const,
      autoComplete: 'off',
      placeholder: '9876543210',
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        event.target.value = toMobileDigits(event.target.value);
        return field.onChange(event);
      },
    };
  }

  return (
    <>
      <StaffFormCard
        icon={IdCard}
        tone="purple"
        title="Identity & statutory numbers"
        description="Stored encrypted. Only staff who can edit this record see them in full."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <AadhaarInput
            label="Aadhaar number"
            error={fieldErrors?.aadhaarNumber?.message}
            {...register('details.aadhaarNumber')}
          />
          <Input
            label="PAN"
            required={isEnrolling}
            placeholder="ABCDE1234F"
            autoComplete="off"
            className="font-mono uppercase"
            maxLength={10}
            error={fieldErrors?.panNumber?.message}
            {...register('details.panNumber')}
          />
          <Input
            label="UAN"
            placeholder="12 digits"
            inputMode="numeric"
            autoComplete="off"
            className="font-mono"
            maxLength={12}
            error={fieldErrors?.uanNumber?.message}
            {...register('details.uanNumber')}
          />
          <Input
            label="PF number"
            placeholder="e.g. MH/BAN/0000064/000/0000123"
            autoComplete="off"
            className="font-mono uppercase"
            maxLength={26}
            error={fieldErrors?.pfNumber?.message}
            {...register('details.pfNumber')}
          />
          <Input
            label="ESI number"
            placeholder="10 or 17 digits"
            inputMode="numeric"
            autoComplete="off"
            className="font-mono"
            maxLength={17}
            error={fieldErrors?.esiNumber?.message}
            {...register('details.esiNumber')}
          />
          <Input
            label="Passport number"
            placeholder="e.g. K1234567"
            autoComplete="off"
            className="font-mono uppercase"
            maxLength={12}
            error={fieldErrors?.passportNumber?.message}
            {...register('details.passportNumber')}
          />
        </div>
      </StaffFormCard>

      <StaffFormCard
        icon={Phone}
        tone="teal"
        title="Contact & address"
        description="Who to reach in an emergency, and where they live."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <Input
            label="Alternate phone"
            error={fieldErrors?.alternateMobile?.message}
            {...mobileField('details.alternateMobile')}
          />
          <Input
            label="Emergency contact name"
            required={isEnrolling}
            placeholder="e.g. Lakshmi Kumar"
            autoComplete="off"
            error={fieldErrors?.emergencyContactName?.message}
            {...register('details.emergencyContactName')}
          />
          <Input
            label="Emergency contact phone"
            required={isEnrolling}
            error={fieldErrors?.emergencyContactMobile?.message}
            {...mobileField('details.emergencyContactMobile')}
          />
        </div>
        <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Textarea
            label="Present address"
            required={isEnrolling}
            rows={3}
            maxLength={250}
            placeholder="House, street, area, city, PIN"
            error={fieldErrors?.presentAddress?.message}
            {...register('details.presentAddress')}
          />
          <Textarea
            label="Permanent address"
            rows={3}
            maxLength={250}
            placeholder="House, street, area, city, PIN"
            error={fieldErrors?.permanentAddress?.message}
            {...register('details.permanentAddress')}
          />
        </div>
      </StaffFormCard>

      <StaffFormCard
        icon={Landmark}
        tone="amber"
        title="Bank details"
        description="How salary is paid, and the account it goes to. The account number is stored encrypted."
      >
        <div className="mb-3.5 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Select
            label="Salary paid by"
            required={isEnrolling}
            options={[NOT_SET_OPTION, ...SALARY_PAYMENT_MODE_OPTIONS]}
            hint={
              isBankTransfer
                ? 'Every bank field below is needed to pay by transfer.'
                : isCash
                  ? 'Paid in cash — no bank account is needed.'
                  : undefined
            }
            error={fieldErrors?.salaryPaymentMode?.message}
            {...register('details.salaryPaymentMode')}
          />
        </div>
        {!isCash && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Input
              label="Bank name"
              required={isBankTransfer}
              placeholder="e.g. State Bank of India"
              autoComplete="off"
              error={fieldErrors?.bankName?.message}
              {...bankField('details.bankName')}
            />
            <Input
              label="Account holder name"
              required={isBankTransfer}
              placeholder="As printed on the passbook"
              autoComplete="off"
              error={fieldErrors?.bankAccountHolder?.message}
              {...bankField('details.bankAccountHolder')}
            />
            <Input
              label="Account number"
              required={isBankTransfer}
              placeholder="9 to 18 digits"
              inputMode="numeric"
              autoComplete="off"
              className="font-mono"
              maxLength={18}
              error={fieldErrors?.bankAccountNumber?.message}
              {...bankField('details.bankAccountNumber')}
            />
            <Input
              label="IFSC"
              required={isBankTransfer}
              placeholder="SBIN0001234"
              autoComplete="off"
              className="font-mono uppercase"
              maxLength={11}
              error={fieldErrors?.bankIfsc?.message}
              {...bankField('details.bankIfsc')}
            />
          </div>
        )}
      </StaffFormCard>

      <StaffFormCard
        icon={Briefcase}
        tone="blue"
        title="Employment details"
        description="The terms they are employed on."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <Select
            label="Employment type"
            required={isEnrolling}
            options={[NOT_SET_OPTION, ...EMPLOYMENT_TYPE_OPTIONS]}
            error={fieldErrors?.employmentType?.message}
            {...register('details.employmentType')}
          />
          <DatePicker
            label="Probation ends on"
            placeholder="Select date"
            error={fieldErrors?.probationEndsOn?.message}
            {...register('details.probationEndsOn')}
          />
          <DatePicker
            label="Confirmation date"
            placeholder="Select date"
            error={fieldErrors?.confirmedOn?.message}
            {...register('details.confirmedOn')}
          />
          <Input
            label="Notice period (days)"
            placeholder="e.g. 30"
            inputMode="numeric"
            autoComplete="off"
            error={fieldErrors?.noticePeriodDays?.message}
            {...register('details.noticePeriodDays')}
          />
          <Input
            label="Work location"
            placeholder="e.g. Main campus"
            autoComplete="off"
            error={fieldErrors?.workLocation?.message}
            {...register('details.workLocation')}
          />
          <Input
            label="Biometric ID"
            placeholder="As set on the attendance device"
            autoComplete="off"
            className="font-mono uppercase"
            maxLength={30}
            error={fieldErrors?.biometricId?.message}
            {...register('details.biometricId')}
          />
          <Select
            label="Reports to"
            options={[{ value: '', label: 'Nobody' }, ...reportingOptions]}
            disabled={isLoadingReporting}
            hint={isLoadingReporting ? 'Loading staff…' : undefined}
            error={fieldErrors?.reportingToUuid?.message}
            {...register('details.reportingToUuid')}
          />
        </div>
      </StaffFormCard>

      <StaffFormCard
        icon={GraduationCap}
        tone="rose"
        title="Additional information"
        description="Background the school office keeps on record."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Input
            label="Languages known"
            placeholder="e.g. Telugu, Hindi, English"
            autoComplete="off"
            error={fieldErrors?.languagesKnown?.message}
            {...register('details.languagesKnown')}
          />
          <Input
            label="Skills"
            placeholder="e.g. Robotics, classical music"
            autoComplete="off"
            error={fieldErrors?.skills?.message}
            {...register('details.skills')}
          />
        </div>
      </StaffFormCard>
    </>
  );
}
